import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { fetchServicePages } from "@/lib/api";
import { useContent, renderAccent } from "@/lib/content";
import Logo from "./Logo";

const LEGAL_LINKS = [
  { to: "/privacybeleid", label: "Privacybeleid" },
  { to: "/cookiebeleid", label: "Cookiebeleid" },
  { to: "/algemene-voorwaarden", label: "Algemene voorwaarden" },
  { to: "/disclaimer", label: "Disclaimer" },
];

export default function Footer({ settings }) {
  const s = settings || {};
  const { c, lines } = useContent();
  const ctaLines = lines("footer.cta.title", ["Klaar om te", "*digitaliseren*?"]);
  const { data: servicePages = [] } = useQuery({ queryKey: ["service-pages"], queryFn: fetchServicePages });
  const expertise = servicePages.map((p) => ({ slug: p.slug, menu_label: p.nav_label || p.title, title: p.title }));

  return (
    <footer
      className="relative overflow-hidden bg-space pt-24 pb-10"
      data-testid="site-footer"
    >
      <div className="atmosphere-field-dark" aria-hidden="true" />
      <div className="relative mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-5">
            <p className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95] text-white">
              {ctaLines.map((l, i) => (
                <span key={i} className="block">{renderAccent(l, "font-serif-italic text-ember")}</span>
              ))}
            </p>
            <Link
              to="/contact"
              className="mt-10 inline-flex items-center gap-3 rounded-full bg-ember text-white px-7 py-4 text-base font-medium hover:bg-ember-hover transition-colors"
              data-testid="footer-cta"
            >
              {c("footer.cta.button", "Plan een gesprek")}
              <ArrowUpRight className="h-5 w-5" />
            </Link>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.2em] text-bell mb-4">Expertise</p>
            <ul className="space-y-2 text-white/60 text-sm">
              {expertise.map((p) => (
                <li key={p.slug}>
                  <Link
                    to={`/diensten/${p.slug}`}
                    className="hover:text-white link-slide"
                    data-testid={`footer-expertise-${p.slug}`}
                  >
                    {p.menu_label || p.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-bell mb-4">Navigatie</p>
            <ul className="space-y-2 text-white/60 text-sm">
              <li><Link to="/diensten" className="hover:text-white link-slide">Diensten</Link></li>
              <li><Link to="/over-mij" className="hover:text-white link-slide">Over mij</Link></li>
              <li><Link to="/cases" className="hover:text-white link-slide">Cases</Link></li>
              <li><Link to="/blog" className="hover:text-white link-slide">Blog</Link></li>
              <li><Link to="/digitaliseringsscan" className="hover:text-white link-slide" data-testid="footer-ai-scan">AI-scan</Link></li>
              <li><Link to="/contact" className="hover:text-white link-slide">Contact</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-bell mb-4">Contact</p>
            <ul className="space-y-2 text-white/60 text-sm">
              <li>
                <a
                  href={`mailto:${s.contact_email || "bastiaan@e-cell.be"}`}
                  className="hover:text-white link-slide"
                  data-testid="footer-email"
                >
                  {s.contact_email || "bastiaan@e-cell.be"}
                </a>
              </li>
              {s.contact_phone && <li>{s.contact_phone}</li>}
              {s.linkedin_url && (
                <li>
                  <a
                    href={s.linkedin_url}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-white link-slide"
                  >
                    LinkedIn
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Juridische links */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-6 border-t border-white/10 text-xs text-white/40">
          {LEGAL_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="hover:text-white/80 transition-colors"
              data-testid={`footer-legal-${l.to.slice(1)}`}
            >
              {l.label}
            </Link>
          ))}
          <button
            onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))}
            className="hover:text-white/80 transition-colors"
            data-testid="footer-cookie-settings"
          >
            Cookie-instellingen
          </button>
          <Link
            to="/admin"
            className="hover:text-white/80 transition-colors"
            data-testid="footer-admin-link"
          >
            Beheer
          </Link>
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-6 mt-6 border-t border-white/10">
          <div className="flex items-center gap-6">
            <Logo size="sm" light />
            <div>
              <p className="text-xs text-bell">{s.tagline || "De groeicel voor kmo's."}</p>
              <p className="text-xs text-white/40 mt-0.5">
                © {new Date().getFullYear()} {s.legal_name || "E-cell Comm.V."} — Bastiaan Phlips
              </p>
            </div>
          </div>
          <p className="text-xs text-white/40" data-testid="footer-legal-info">
            {s.registered_address || "Beuntstraat 37, 2570 Duffel"} · BTW {s.vat_number || "BE 0847.476.132"}
          </p>
        </div>
      </div>
    </footer>
  );
}
